import { useState, useEffect, useCallback } from 'react';
import { AlarmClock, AlertTriangle, CalendarClock, RefreshCw, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ledgersAPI } from '@/services/api';

const money = (v) => `₹${Math.round(Number(v) || 0).toLocaleString('en-IN')}`;
const dateOnly = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusStyles = {
  overdue: { row: 'bg-red-50', badge: 'bg-red-600 text-white', label: 'Overdue' },
  due_today: { row: 'bg-amber-100', badge: 'bg-amber-600 text-white', label: 'Due today' },
  due_soon: { row: 'bg-amber-50', badge: 'bg-amber-500 text-white', label: 'Due soon' },
  upcoming: { row: '', badge: 'bg-gray-200 text-gray-700', label: 'Upcoming' }
};

export const VendorPaymentDuePanel = ({ vendorId = null, onPay = null, compact = false, refreshKey = 0, fixedStatus = null }) => {
  const [data, setData] = useState({ summary: {}, vendors: [], bills: [] });
  const [daysAhead, setDaysAhead] = useState(fixedStatus === 'upcoming' ? '30' : '7');
  const [statusFilter, setStatusFilter] = useState(fixedStatus || 'all');
  const minimal = !!fixedStatus;
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days_ahead: Number(daysAhead) };
      if (vendorId) params.vendor_id = vendorId;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await ledgersAPI.vendorPaymentReminders(params);
      setData(res.data);
    } catch (error) {
      setData({ summary: {}, vendors: [], bills: [] });
    } finally {
      setLoading(false);
    }
  }, [vendorId, daysAhead, statusFilter, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const s = data.summary || {};

  return (
    <div className="space-y-4" data-testid="payment-due-panel">
      <div className="flex flex-wrap items-center gap-3">
        {!minimal && (
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <AlarmClock className="w-5 h-5 text-red-500" /> Payment Due Reminders
          </h2>
        )}
        <span className="text-sm text-gray-500">Based on each vendor&apos;s credit days</span>
        <div className="ml-auto flex items-center gap-2">
          {fixedStatus !== 'overdue' && <Select value={daysAhead} onValueChange={setDaysAhead}>
            <SelectTrigger className="w-36 bg-white border-gray-300" data-testid="due-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['3', '7', '15', '30'].map(d => (
                <SelectItem key={d} value={d}>Next {d} days</SelectItem>
              ))}
            </SelectContent>
          </Select>}
          {!minimal && <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-white border-gray-300" data-testid="due-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All pending</SelectItem>
              <SelectItem value="overdue">Overdue only</SelectItem>
              <SelectItem value="due_today,due_soon">Due soon</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
            </SelectContent>
          </Select>}
          <Button variant="outline" onClick={load} className="border-gray-300" data-testid="due-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!minimal && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-red-600 border-red-700">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-red-100">Overdue</p>
            <p className="text-2xl font-bold text-white" data-testid="due-overdue-amount">{money(s.overdue_amount)}</p>
            <p className="text-xs text-red-100">{s.overdue_count || 0} bill(s)</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-amber-600">Due in {daysAhead} days</p>
            <p className="text-2xl font-bold text-amber-700" data-testid="due-soon-amount">{money(s.due_soon_amount)}</p>
            <p className="text-xs text-amber-600">{s.due_soon_count || 0} bill(s)</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-gray-400">Total pending</p>
            <p className="text-2xl font-bold text-gray-800">{money(s.total_pending)}</p>
            <p className="text-xs text-gray-500">{s.bill_count || 0} bill(s)</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-gray-400">
              {statusFilter === 'all' ? 'Vendors with dues' : 'Vendors in view'}
            </p>
            <p className="text-2xl font-bold text-gray-800">{(data.vendors || []).length}</p>
            <p className="text-xs text-gray-500">Oldest due first below</p>
          </CardContent>
        </Card>
      </div>}

      {!vendorId && !compact && !minimal && (data.vendors || []).length > 0 && (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-center">Credit days</TableHead>
                  <TableHead className="text-center">Bills</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead>Oldest due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.vendors.map(v => (
                  <TableRow key={v.vendor_id} className={v.overdue_count ? 'bg-red-50' : ''}
                    data-testid={`due-vendor-row-${v.vendor_id}`}>
                    <TableCell className="font-medium text-gray-800">{v.vendor_name}</TableCell>
                    <TableCell className="text-center text-sm text-gray-600">{v.credit_days}</TableCell>
                    <TableCell className="text-center text-sm">{v.bill_count}</TableCell>
                    <TableCell className="text-right text-red-600 font-medium">
                      {v.overdue_count ? `${money(v.overdue_amount)} (${v.overdue_count})` : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">{money(v.pending_amount)}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {dateOnly(v.oldest_due_date)}
                      {v.max_days_overdue ? <span className="text-red-600"> · {v.max_days_overdue}d late</span> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0 overflow-auto max-h-[560px]">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                <TableHead>Status</TableHead>
                {!vendorId && <TableHead>Vendor</TableHead>}
                <TableHead>GRN / Invoice</TableHead>
                <TableHead>Invoice date</TableHead>
                <TableHead>Terms</TableHead>
                <TableHead>Due date</TableHead>
                <TableHead className="text-right">Bill</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                {onPay && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-400">Loading...</TableCell></TableRow>
              ) : (data.bills || []).length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-400">
                  No pending vendor bills for this filter
                </TableCell></TableRow>
              ) : data.bills.map(b => {
                const st = statusStyles[b.status] || statusStyles.upcoming;
                return (
                  <TableRow key={b.ledger_entry_id} className={st.row} data-testid={`due-bill-row-${b.ledger_entry_id}`}>
                    <TableCell>
                      <Badge className={st.badge}>
                        {b.status === 'overdue' ? <AlertTriangle className="w-3 h-3 mr-1" /> : <CalendarClock className="w-3 h-3 mr-1" />}
                        {st.label}
                      </Badge>
                      <span className="block text-xs text-gray-500 mt-1">
                        {b.status === 'overdue' ? `${b.days_overdue} day(s) late` :
                          b.status === 'due_today' ? 'today' : `in ${b.days_to_due} day(s)`}
                      </span>
                    </TableCell>
                    {!vendorId && <TableCell className="text-sm font-medium text-gray-800">{b.vendor_name}</TableCell>}
                    <TableCell className="text-sm">
                      {b.grn_number || '-'}
                      <span className="block text-xs text-gray-400">{b.invoice_number || 'No invoice no.'}</span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{dateOnly(b.invoice_date)}</TableCell>
                    <TableCell className="text-sm text-gray-600">{b.credit_days} days</TableCell>
                    <TableCell className={`text-sm font-medium ${b.status === 'overdue' ? 'text-red-600' : 'text-gray-700'}`}>
                      {dateOnly(b.due_date)}
                    </TableCell>
                    <TableCell className="text-right text-sm">{money(b.bill_amount)}</TableCell>
                    <TableCell className="text-right text-sm text-emerald-600">{money(b.paid_amount)}</TableCell>
                    <TableCell className="text-right font-semibold">{money(b.pending_amount)}</TableCell>
                    {onPay && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => onPay(b)}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`due-pay-btn-${b.ledger_entry_id}`}>
                          <CreditCard className="w-3 h-3 mr-1" /> Pay
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorPaymentDuePanel;
